import { ActionIcon, Badge, Button, Collapse, Group, Paper, Stack, Text, TextInput } from '@mantine/core';
import { IconChevronDown, IconChevronRight, IconChevronUp, IconPlus, IconTrash } from '@tabler/icons-react';
import { useState } from 'react';
import {
  MAX_CONSENT_PROVIDERS,
  MAX_PROVIDER_NAME_LENGTH,
  MAX_PROVIDER_SELECTORS,
  MAX_SELECTOR_LENGTH,
  MIN_PROVIDER_SELECTORS,
  blankConsentProvider,
  consentProviderErrors,
  duplicateProviderNames,
} from '../../helpers/knowledgeIngest';

const plural = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`;

/**
 * The client's consent-banner providers: one collapsible row per provider with its selectors
 * inside. Controlled — `onChange` gets the whole new list. Rows are keyed by position (as in
 * BrowserStepsEditor), so open rows are tracked by position and shifted when one above is removed.
 * The caller keys this by chat bot id, so open rows never carry over to another client's list.
 */
const ConsentProvidersEditor = ({ providers, onChange }) => {
  const [listOpen, setListOpen] = useState(false);
  const [openRows, setOpenRows] = useState([]);
  const duplicates = duplicateProviderNames(providers);

  // Empty fields are reported by Save, not while being filled in. Guarded the same way
  // consentProviderErrors is, so a malformed row (never written today, but not impossible) renders
  // instead of throwing during render.
  const rows = providers.map((provider) => {
    const errors = consentProviderErrors(provider, duplicates);
    const name = typeof provider?.name === 'string' ? provider.name : '';
    const selectors = Array.isArray(provider?.selectors) ? provider.selectors : [];
    const nameError = name.trim() ? errors.name : null;
    const selectorErrors = selectors.map((selector, position) =>
      typeof selector === 'string' && selector.trim() ? errors.selectors[position] : null
    );
    return { nameError, selectorErrors, needsFix: Boolean(nameError) || selectorErrors.some(Boolean) };
  });
  const needsFixCount = rows.filter((row) => row.needsFix).length;

  const updateProvider = (index, changes) =>
    onChange(providers.map((provider, position) => (position === index ? { ...provider, ...changes } : provider)));

  const toggleRow = (index) =>
    setOpenRows((open) => (open.includes(index) ? open.filter((row) => row !== index) : [...open, index]));

  const addProvider = () => {
    onChange([...providers, blankConsentProvider()]);
    setOpenRows((open) => [...open, providers.length]);
  };

  const removeProvider = (index) => {
    onChange(providers.filter((_, position) => position !== index));
    setOpenRows((open) => open.filter((row) => row !== index).map((row) => (row > index ? row - 1 : row)));
  };

  return (
    <>
      <Group gap="xs">
        <Text size="sm">{plural(providers.length, 'provider')}</Text>
        {needsFixCount > 0 && (
          <Badge color="red" variant="light" size="sm" tt="none">
            {needsFixCount} {needsFixCount === 1 ? 'needs' : 'need'} a fix
          </Badge>
        )}
        <Button
          size="xs"
          variant="subtle"
          color="gray"
          aria-expanded={listOpen}
          rightSection={listOpen ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
          onClick={() => setListOpen((open) => !open)}
        >
          {listOpen ? 'Hide providers' : 'Show providers'}
        </Button>
      </Group>

      <Collapse expanded={listOpen}>
        <Stack gap={6} mt="xs">
          {providers.map((provider, index) => {
            const name = typeof provider?.name === 'string' ? provider.name : '';
            const selectors = Array.isArray(provider?.selectors) ? provider.selectors : [];
            const title = name.trim() || `Provider ${index + 1}`;
            const open = openRows.includes(index);
            const { nameError, selectorErrors, needsFix } = rows[index];
            const setSelectors = (nextSelectors) => updateProvider(index, { selectors: nextSelectors });

            return (
              <Paper key={index} withBorder radius="sm" p="xs">
                <Group gap={8} wrap="nowrap">
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    aria-expanded={open}
                    aria-label={`${open ? 'Hide' : 'Show'} ${title} selectors`}
                    onClick={() => toggleRow(index)}
                  >
                    {open ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
                  </ActionIcon>
                  <Text size="sm" fw={500} truncate style={{ flex: 1, minWidth: 0 }}>
                    {title}
                  </Text>
                  {needsFix && (
                    <Badge color="red" variant="light" size="sm" tt="none">
                      needs a fix
                    </Badge>
                  )}
                  <Badge variant="default" size="sm" tt="none">
                    {plural(selectors.length, 'selector')}
                  </Badge>
                  <ActionIcon
                    variant="default"
                    color="red"
                    aria-label={`Remove provider ${title}`}
                    onClick={() => removeProvider(index)}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>

                <Collapse expanded={open}>
                  <Stack gap={6} mt="xs" pl={36}>
                    <TextInput
                      size="xs"
                      label="Name"
                      maxLength={MAX_PROVIDER_NAME_LENGTH}
                      value={name}
                      error={nameError}
                      onChange={(event) => updateProvider(index, { name: event.currentTarget.value })}
                    />
                    {selectors.map((selector, position) => (
                      <Group key={position} gap={6} wrap="nowrap" align="flex-start">
                        <TextInput
                          size="xs"
                          style={{ flex: 1 }}
                          aria-label={`${title} selector ${position + 1}`}
                          placeholder="#cookie-banner"
                          maxLength={MAX_SELECTOR_LENGTH}
                          value={selector}
                          error={selectorErrors[position]}
                          styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }}
                          onChange={(event) => {
                            const value = event.currentTarget.value;
                            setSelectors(selectors.map((current, i) => (i === position ? value : current)));
                          }}
                        />
                        <ActionIcon
                          variant="default"
                          color="red"
                          aria-label={`Remove ${title} selector ${position + 1}`}
                          disabled={selectors.length <= MIN_PROVIDER_SELECTORS}
                          onClick={() => setSelectors(selectors.filter((_, i) => i !== position))}
                        >
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Group>
                    ))}
                    <Button
                      size="xs"
                      variant="default"
                      leftSection={<IconPlus size={14} />}
                      disabled={selectors.length >= MAX_PROVIDER_SELECTORS}
                      onClick={() => setSelectors([...selectors, ''])}
                      style={{ alignSelf: 'flex-start' }}
                    >
                      Add selector
                    </Button>
                  </Stack>
                </Collapse>
              </Paper>
            );
          })}

          <Button
            variant="default"
            leftSection={<IconPlus size={16} />}
            disabled={providers.length >= MAX_CONSENT_PROVIDERS}
            onClick={addProvider}
            style={{ alignSelf: 'flex-start' }}
          >
            Add provider
          </Button>
        </Stack>
      </Collapse>
    </>
  );
};

export default ConsentProvidersEditor;
